import { Radio, RadioGroup } from 'baseui/radio';
import { useSnackbar } from 'baseui/snackbar';
import { useFormikContext } from 'formik';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLocationArrow } from 'react-icons/fa';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { ApiException } from '../../api/api.generated';
import { useSendGeoLinkSms } from '../../api/queries/useCommunicationClient';
import { FastInputField as InputField } from '../../shared/formFields/InputField';
import { isPhoneNumberValid } from '../../shared/functions/general';
import { ActorsEnum } from '../../shared/types';
import FormGroup from '../../shared/utilityComponents/FormGroup';
import PopoverButton from '../../shared/utilityComponents/PopoverButton';
import { nameof } from '../caseRegistrationFunctions';
import { CaseRegistrationFormFields } from '../caseRegistrationModels';

function SendGeoLinkFormGroupComp() {
  const tShared = useTranslation('translation', { keyPrefix: 'shared' }).t;
  const tRequester = useTranslation('translation', { keyPrefix: 'requesterForm' }).t;

  const formik = useFormikContext<CaseRegistrationFormFields>();
  const [selectedRequester, setSelectedRequester] = useState<ActorsEnum>();

  const { visCaseId, caseId } = formik.values;
  const callerPhone = formik.values.callerPhone;
  const ownerPhone = formik.values.ownerPhone;
  const selectedPhoneNumber = selectedRequester === ActorsEnum.owner ? ownerPhone : callerPhone;

  const { enqueue } = useSnackbar();
  const sendSms = useSendGeoLinkSms();

  return (
    <>
      <FormGroup label={tRequester('sendgeolink')}>
        <RadioGroup
          value={selectedRequester}
          onChange={(e) => setSelectedRequester(e.target.value as ActorsEnum)}
          name="tempRequester"
        >
          <Radio value={ActorsEnum.owner} disabled={!isPhoneNumberValid(ownerPhone)}>
            {tShared('owner')}
          </Radio>
          <Radio value={ActorsEnum.caller} disabled={!isPhoneNumberValid(callerPhone)}>
            {tShared('caller')}
          </Radio>
        </RadioGroup>
      </FormGroup>
      {selectedRequester ? (
        <>
          <InputField
            readOnly
            label={tShared('name')}
            name={selectedRequester === ActorsEnum.owner ? nameof('ownerName') : nameof('callerName')}
          />
          <InputField
            readOnly
            label={tShared('telephoneNumber')}
            name={selectedRequester === ActorsEnum.owner ? nameof('ownerPhone') : nameof('callerPhone')}
          />
        </>
        ) : null}
      <PopoverButton
        kind="secondary"
        type="button"
        startEnhancer={() => <FaLocationArrow />}
        disabled={!visCaseId || !isPhoneNumberValid(selectedPhoneNumber)}
        isLoading={sendSms.isLoading}
        onClick={() => {
          if (visCaseId && caseId) {
            sendSms
              .mutateAsync({
                caseId,
                phoneNumber: selectedPhoneNumber,
                visCaseId: parseInt(visCaseId),
                messageText: '', // Isn't needed for GeoLinkSms, but is required in API, so we send empty string always.
              })
              .then(() => {
                enqueue({
                  message: tRequester('geoLinkSent'),
                  startEnhancer: () => <MdCheckCircle size={22} />,
                });
              })
              .catch((e: ApiException) => {
                enqueue({
                  message: `${tShared('error')}: ${e.response}`,
                  startEnhancer: () => <MdError size={22} />,
                });
              });
          }
        }}
        content={tRequester('sendGeolinkRequirements')}
      >
        {tRequester('sendgeolink')}
      </PopoverButton>
      </>
  );
}

export const SendGeoLinkFormGroup = memo(SendGeoLinkFormGroupComp);
 